import React, { useState } from 'react';
import { authService } from '../services/authService';
import { UserRole } from '../types';
import { extractErrorMessage, reportFormError } from '../utils/errorHandling';

interface AddUserFormProps {
  onUserListUpdate?: () => void;
}

const AddUserForm: React.FC<AddUserFormProps> = ({ onUserListUpdate }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('manager');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authService.register({
        firstName,
        lastName,
        email,
        role,
      });

      if (response.success && response.data) {
        setSuccess(`User created successfully! Temporary password: ${response.data.temporaryPassword}`);
        // Clear form
        setFirstName('');
        setLastName('');
        setEmail('');
        setRole('manager');
        // Notify parent component to refresh user list
        onUserListUpdate?.();
      } else {
        throw new Error(response.error || 'Failed to create user');
      }
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to create user.');
      setError(message);
      reportFormError({
        formName: 'Add User',
        error: err,
        message,
        tags: ['user-management'],
        userEmail: email,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-2xl font-bold text-primary-900">Add New User</h2>
      </div>
      <div className="card-body">
        {error && <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg relative mb-6 font-medium" role="alert">{error}</div>}
        {success && <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-lg relative mb-6 font-medium" role="alert">{success}</div>}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="form-label" htmlFor="firstName">
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="form-input"
              placeholder="Enter first name"
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="lastName">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="form-input"
              placeholder="Enter last name"
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder="Enter email address"
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="role">
              Role
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="form-input"
            >
              <option value="manager">Manager</option>
              <option value="hr_clerk">HR Clerk</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer (Read-only)</option>
            </select>
          </div>
          <div className="pt-4">
            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserForm;
